import { useMemo, useState } from 'react';
import { ImageOff, Minus, Plus, ShoppingBag } from 'lucide-react';
import type { ProductOptionGroup } from '@kick/types';
import { cn, formatCOP } from '@/lib/cn';
import {
  useCarrito,
  validarSeleccion,
  type ComercioCarrito,
  type OpcionElegida,
} from '@/lib/carrito';
import type { ProductoPublico } from '@/lib/queries';
import { AreaTexto, Boton, Modal } from './ui';
import { useAvisos } from './avisos';

export function ModalProducto({
  producto,
  comercio,
  abierto,
  onCerrar,
}: {
  producto: ProductoPublico;
  comercio: ComercioCarrito;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const agregar = useCarrito((s) => s.agregar);
  const requiereCambiar = useCarrito((s) => s.requiereCambiarComercio);
  const comercioActual = useCarrito((s) => s.comercio);
  const avisos = useAvisos();

  const [elegidas, setElegidas] = useState<OpcionElegida[]>([]);
  const [cantidad, setCantidad] = useState(1);
  const [notas, setNotas] = useState('');
  const [intentado, setIntentado] = useState(false);
  const [confirmarCambio, setConfirmarCambio] = useState(false);

  const errores = useMemo(
    () => validarSeleccion(producto.optionGroups, elegidas),
    [producto.optionGroups, elegidas],
  );

  /** El precio se recalcula con cada opción: el cliente ve lo que va a pagar. */
  const precioUnitario = useMemo(
    () => producto.price + elegidas.reduce((suma, o) => suma + o.priceDelta, 0),
    [producto.price, elegidas],
  );

  const alternar = (grupo: ProductOptionGroup, opcion: ProductOptionGroup['options'][number]): void => {
    setElegidas((actuales) => {
      const yaEsta = actuales.some((o) => o.optionId === opcion.id);
      const delGrupo = actuales.filter((o) => o.groupId === grupo.id);

      if (yaEsta) return actuales.filter((o) => o.optionId !== opcion.id);

      // En los grupos de selección única, elegir reemplaza lo anterior.
      const sinGrupo =
        grupo.maxSelect === 1 ? actuales.filter((o) => o.groupId !== grupo.id) : actuales;

      if (grupo.maxSelect > 1 && delGrupo.length >= grupo.maxSelect) {
        avisos.error(`Puedes elegir máximo ${grupo.maxSelect} en «${grupo.name}»`);
        return actuales;
      }

      return [
        ...sinGrupo,
        {
          groupId: grupo.id,
          groupName: grupo.name,
          optionId: opcion.id,
          optionName: opcion.name,
          priceDelta: opcion.priceDelta,
        },
      ];
    });
  };

  const confirmar = (): void => {
    setIntentado(true);
    if (errores.length > 0) return;

    if (requiereCambiar(comercio.id)) {
      setConfirmarCambio(true);
      return;
    }

    agregar(comercio, producto, elegidas, cantidad, notas);
    avisos.exito(`${producto.name} agregado al carrito`);
    onCerrar();
  };

  const aceptarCambio = (): void => {
    agregar(comercio, producto, elegidas, cantidad, notas);
    avisos.exito('Carrito reiniciado con tu nuevo pedido');
    setConfirmarCambio(false);
    onCerrar();
  };

  // Confirmación antes de descartar el carrito de otro comercio.
  if (confirmarCambio) {
    return (
      <Modal
        abierto
        onCerrar={() => setConfirmarCambio(false)}
        titulo="¿Empezar un pedido nuevo?"
        ancho="sm"
      >
        <div className="p-5">
          <p className="text-kick-ink">
            Tu carrito tiene productos de <strong>{comercioActual?.name}</strong>. Cada pedido se
            prepara y se recoge en un solo comercio, así que al agregar de{' '}
            <strong>{comercio.name}</strong> se vaciará lo anterior.
          </p>
          <div className="mt-6 flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => setConfirmarCambio(false)}>
              Conservar mi carrito
            </Boton>
            <Boton onClick={aceptarCambio}>Empezar de nuevo</Boton>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal abierto={abierto} onCerrar={onCerrar} titulo={producto.name} ancho="md">
      <div className="max-h-[70vh] overflow-y-auto">
        {producto.imageUrl ? (
          <img
            src={producto.imageUrl}
            alt=""
            className="h-56 w-full bg-kick-light object-contain sm:h-64"
          />
        ) : (
          <div className="grid h-32 place-items-center bg-kick-light text-kick-gray/40">
            <ImageOff className="h-8 w-8" />
          </div>
        )}

        <div className="p-5">
          {producto.description && <p className="text-kick-gray">{producto.description}</p>}

          <p className="mt-3 text-2xl font-bold text-kick-dark">{formatCOP(producto.price)}</p>

          {producto.optionGroups.map((grupo) => {
            const error = intentado ? errores.find((e) => e.groupId === grupo.id) : undefined;
            const delGrupo = elegidas.filter((o) => o.groupId === grupo.id).length;

            return (
              <fieldset key={grupo.id} className="mt-6">
                <legend className="flex w-full items-center justify-between">
                  <span className="font-semibold text-kick-ink">{grupo.name}</span>
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs font-semibold',
                      grupo.isRequired
                        ? 'bg-kick-red/10 text-kick-red'
                        : 'bg-kick-light text-kick-gray',
                    )}
                  >
                    {grupo.isRequired ? 'Obligatorio' : 'Opcional'}
                    {grupo.maxSelect > 1 && ` · hasta ${grupo.maxSelect}`}
                  </span>
                </legend>

                {error && <p className="mt-1 text-sm text-red-600">{error.mensaje}</p>}

                <div className="mt-2 space-y-1.5">
                  {grupo.options
                    .filter((o) => o.isAvailable)
                    .map((opcion) => {
                      const elegida = elegidas.some((o) => o.optionId === opcion.id);
                      const bloqueada =
                        !elegida && grupo.maxSelect > 1 && delGrupo >= grupo.maxSelect;

                      return (
                        <label
                          key={opcion.id}
                          className={cn(
                            'flex cursor-pointer items-center gap-3 rounded-kick border px-3.5 py-3 transition',
                            elegida
                              ? 'border-kick-red bg-kick-red/5'
                              : 'border-kick-gray/20 hover:border-kick-gray/40',
                            bloqueada && 'cursor-not-allowed opacity-50',
                          )}
                        >
                          <input
                            type={grupo.maxSelect === 1 ? 'radio' : 'checkbox'}
                            name={grupo.id}
                            checked={elegida}
                            disabled={bloqueada}
                            onChange={() => alternar(grupo, opcion)}
                            className="h-4 w-4 accent-kick-red"
                          />
                          <span className="flex-1 text-kick-ink">{opcion.name}</span>
                          {opcion.priceDelta !== 0 && (
                            <span className="text-sm font-semibold text-kick-gray">
                              {opcion.priceDelta > 0 ? '+' : ''}
                              {formatCOP(opcion.priceDelta)}
                            </span>
                          )}
                        </label>
                      );
                    })}
                </div>
              </fieldset>
            );
          })}

          <div className="mt-6">
            <label htmlFor="notas" className="mb-1.5 block font-semibold text-kick-ink">
              Notas para el comercio
            </label>
            <AreaTexto
              id="notas"
              rows={2}
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Sin cebolla, término medio…"
              maxLength={200}
            />
          </div>
        </div>
      </div>

      {/* Barra de acción, siempre visible */}
      <div className="flex items-center gap-3 border-t border-kick-gray/15 p-4">
        <div className="flex items-center gap-1 rounded-kick border border-kick-gray/25">
          <button
            onClick={() => setCantidad((c) => Math.max(1, c - 1))}
            disabled={cantidad <= 1}
            aria-label="Quitar uno"
            className="p-2.5 text-kick-gray transition hover:text-kick-ink disabled:opacity-30"
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="w-8 text-center font-semibold text-kick-ink" aria-live="polite">
            {cantidad}
          </span>
          <button
            onClick={() => setCantidad((c) => Math.min(50, c + 1))}
            aria-label="Agregar uno"
            className="p-2.5 text-kick-gray transition hover:text-kick-ink"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        <Boton
          onClick={confirmar}
          className="flex-1"
          icono={<ShoppingBag className="h-4 w-4" />}
        >
          Agregar · {formatCOP(precioUnitario * cantidad)}
        </Boton>
      </div>
    </Modal>
  );
}
